import { CircularProgress } from "@mui/material";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import restClient from "api/restClient";
import { graphqlPost } from "api/graphqlClient";
import { schemaFormSchemaSelector } from "components/Form/components/SchemaForm/SchemaForm.selectors";
import { DefaultPage } from "components/Layout/DefaultPage";
import { KIOSK_QUERY_FLAG } from "utils/kiosk";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { NavigationPromptProvider } from "components/Prompt/NavigationPromptContext";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import {
    getCanRedirectToWorkspace,
    getSignedIn,
    getUserData,
} from "containers/Auth/Auth.selectors";
import AuthPage from "containers/Auth/AuthPage";
import { loginCheckUrl } from "containers/Auth/constants";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { getProjectsById } from "containers/Projects/Projects.selectors";
import {
    getKeyToForceUpdate,
    gitLogPlainListSelector,
} from "containers/Resources/Resources.selectors";
import { metaTypes } from "containers/Resources/components/MetaType/MetaType.service";
import { getAllUncommittedGitLogData } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { useGitWriteLock } from "containers/Resources/screens/ResourcesSingle/components/GitAction/gitWriteLock/useGitWriteLock";
import { fetchSchemaFormSchema, isMonacoEditorActiveElement } from "containers/Resources/screens/ResourcesSingle/utils";
import { cloneDeep } from "lodash";
import PropTypes from "prop-types";
import qs from "qs";
import { Suspense, createContext, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory, useRouteMatch, withRouter } from "react-router-dom";
import { useMount } from "react-use";
import { actions as dataStoreActions } from "utils/DataStore/DataStore.reducer";
import MultiWebsocket from "utils/MultiWebsocket/MultiWebsocket";
import { actions as notificationManagerActions } from "utils/NotificationManager/NotificationManager.reducer";
import { bootstrapUserNameKey } from "utils/RouterManager/constants";
import { stripFilterFromSearch } from "utils/RouterManager/searchParams";
import { getRelativeUrl, getTypedLowerCase, getWorkspaceLink, getWorkspaceUrl } from "utils/service";
import { useIsNarrow } from "utils/useMediaQuery";
import {
    MobileInterstitial,
    dismissMobileInterstitial,
    isMobileInterstitialDismissed,
    shouldShowMobileInterstitial,
} from "components/MobileInterstitial";
import RouterChangeHandler from "./RouterChangeHandler";
import "./RouterManager.scss";
import {
    activatePath,
    advancedSearchPathFragment,
    advancedSearchPathLegacy,
    bundleManagerPathFragment,
    canvasPathFragment,
    dashboardViewPathFragment,
    dashboardEditPathFragment,
    confirmEmailPath,
    deletedPathLegacy,
    executionsPathFragment,
    logListPathFragment,
    logSubPageKey,
    loginPath,
    recoverPath,
    resetPath,
    settingsPathFragment,
    signOutPath,
    signedOutRoutesArray,
    storageAnalysisSubPageKey,
    usersRolesManagementPathLegacy,
    usersRolesPathFragment,
} from "./constants";
import { mapScreensToPaths } from "./utils";
import { useMaxUICountNotification } from "utils/useMaxUICountNotification";
import { RouteErrorBoundary } from "utils/RouteErrorBoundary";
import { lazyWithReload } from "utils/staleChunkReload";
import { isAuthCheckUnauthorized } from "utils/authCheckErrors";
import { recordList } from "utils/utils";

const Error = lazyWithReload(() => import("utils/RouterManager/screens/Error"));
export const RouterContext = createContext();

// Auth
const Login = lazyWithReload(() => import("containers/Auth/screens/Login"));
const ActivateAccount = lazyWithReload(() => import("containers/Auth/screens/ActivateAccount"));
const ConfirmEmail = lazyWithReload(() => import("containers/Auth/screens/ConfirmEmail"));
const SignOut = lazyWithReload(() => import("containers/Auth/screens/SignOut"));
const RecoverAccount = lazyWithReload(() => import("containers/Auth/screens/RecoverAccount"));
const ResetPassword = lazyWithReload(() => import("containers/Auth/screens/ResetPassword"));

//  App
const ResourcesSingle = lazyWithReload(() => import("containers/Resources/screens/ResourcesSingle"));
const NewResourceSingle = lazyWithReload(() =>
    import("containers/Resources/screens/ResourcesSingle/NewResourceSingle")
);
const AdvancedSearch = lazyWithReload(() => import("containers/AdvancedSearch"));
const TrashBin = lazyWithReload(() => import("containers/TrashBin/TrashBin"));
const ExecutionsListing = lazyWithReload(() => import("containers/Executions/screens/ExecutionsListing"));
const IdentityManagement = lazyWithReload(() => import("containers/Auth/screens/IdentityManagement"));
const ContainerLogList = lazyWithReload(() =>
    import("containers/Resources/screens/ResourcesSingle/components/RecordLogList/ContainerLogList")
);
const BundleManager = lazyWithReload(() => import("containers/BundleManager"));
const WorkspaceSettings = lazyWithReload(() => import("containers/WorkspaceSettings"));
const StorageAnalysis = lazyWithReload(() => import("containers/StorageAnalysis/StorageAnalysis"));
const MetaType = lazyWithReload(() => import("containers/Resources/components/MetaType/MetaType"));
const Canvas = lazyWithReload(() => import("components/ObjectTemplateCanvas/Canvas"));
const DashboardView = lazyWithReload(() => import("containers/Dashboard/DashboardView"));

const ajv = new Ajv({ strict: false });
addFormats(ajv);
/**
 * Router Manager
 * @return {Node} Returns current active route component
 */
const RouterManager = ({ location }) => {
    const initialUserData = useRef({});
    const dispatch = useDispatch();
    const isSignedIn = useSelector(getSignedIn);
    const canRedirectToWorkspace = useSelector(getCanRedirectToWorkspace);
    const userData = useSelector(getUserData);
    const workspaceID = userData?.workspace_id ?? initialUserData.current?.workspace_id;
    const allowedToClearNotifications = useRef(false);
    const { pathname, search } = location;
    const queryParams = useMemo(() => qs.parse(search?.substring(1) ?? ""), [search]);
    const isKiosk = KIOSK_QUERY_FLAG in queryParams;
    const recordId = queryParams?.id ?? queryParams?.name;
    const newName = queryParams?.name;

    const keyToForceUpdate = useSelector(getKeyToForceUpdate);

    const history = useHistory();

    const matchOldRouteVersion = useRouteMatch("/:resourceName/:resourceId");
    const resourceName = matchOldRouteVersion?.params?.resourceName;
    const resourceId = matchOldRouteVersion?.params?.resourceId;

    const isNewRecord = resourceId === "new";

    const matchRouteWithTwoSubfolders = useRouteMatch(
        "/:resourceName/:resourceId/:anotherSubfolder"
    );
    const schemaFormSchema = useSelector(schemaFormSchemaSelector);
    const filteredSchemaFormSchema = useMemo(() => {
        const schema = cloneDeep(schemaFormSchema) ?? {};
        const anyOf = (schema.anyOf ?? schema.oneOf ?? []).filter(
            (item) => !item.label.includes("recursion")
        );
        return { ...schema, anyOf };
    }, [schemaFormSchema]);

    const gitLogPlainList = useSelector(gitLogPlainListSelector);
    const allUncommittedData = useMemo(
        () => getAllUncommittedGitLogData(gitLogPlainList),
        [gitLogPlainList]
    );

    const gitLockData = useGitWriteLock();
    const prevGitLockDataRef = useRef({});

    const projectsById = useSelector(getProjectsById);
    const bundlesById = useSelector(getBundlesById);

    useEffect(() => {
        for (const gitLockItem in gitLockData) {
            const current = gitLockData[gitLockItem];
            const previous = prevGitLockDataRef.current[gitLockItem];
            const hasNewError =
                current?.state === "WRITE_FAILED" && previous?.state !== "WRITE_FAILED";
            if (hasNewError) {
                const type = current?.record?.type.toLowerCase();
                const id = current?.record?.id;
                const name =
                    type === "project"
                        ? projectsById[id]?.name
                        : type === "bundle"
                        ? bundlesById[id]?.name
                        : null;
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride={`Git action failed for ${type} ${
                            name ? `"${name}"` : `with id ${id}`
                        }`}
                        error={current?.exception}
                    />
                );
            }
        }
        prevGitLockDataRef.current = { ...gitLockData };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gitLockData]);

    const navigateToNewRoute = () => {
        if (matchOldRouteVersion && !matchRouteWithTwoSubfolders && !isNewRecord) {
            history.push(getRelativeUrl(resourceName, resourceId, "id"));
        }
    };

    const { canConnect } = useMaxUICountNotification(workspaceID);

    useMount(async () => {
        const runAuthCheck = async (attempt = 0) => {
            try {
                const res = await restClient.get(loginCheckUrl);
                initialUserData.current = res.data;

                if (res?.data?.is_engine_user === false) {
                    NotificationService.warn(
                        "Your user is not authorized to use the Cloudomation Engine UI. Please talk to your Cloudomation administrator if you need access to the Cloudomation Engine UI."
                    );

                    return new Promise((resolve, reject) =>
                        dispatch({
                            promise: { resolve, reject },
                            type: authActions.START_SIGN_OUT,
                        })
                    ).then(() => history.push(loginPath));
                }

                // Seed userData from the /auth/check response so the workspace
                // id (and the rest of the identity) is available immediately,
                // instead of waiting for the user-data websocket message. This
                // lets the workspace dashboard start loading right away and
                // avoids briefly rendering the not-found screen for a still
                // unknown workspace id. /auth/check returns a superset of the
                // websocket identity payload, so this is shape-compatible; the
                // websocket's STORE_USER_DATA later refreshes it.
                dispatch({
                    type: authActions.STORE_USER_DATA,
                    response: res.data,
                });
                dispatch({
                    type: authActions.SIGN_IN_SUCCESSFUL,
                });
                if (res?.data?.backend_version != null) {
                    dispatch({
                        type: authActions.STORE_BACKEND_VERSION,
                        response: res.data.backend_version,
                    });
                }
                const publicInfoUpdate = {};
                if (res?.data?.workspace_name != null) {
                    publicInfoUpdate.workspaceName = res.data.workspace_name;
                }
                if (res?.data?.workspace_highlight_color != null) {
                    publicInfoUpdate.workspaceHighlightColor = res.data.workspace_highlight_color;
                }
                if (res?.data?.backend_version != null) {
                    publicInfoUpdate.backendVersion = res.data.backend_version;
                }
                if (Object.keys(publicInfoUpdate).length > 0) {
                    dispatch({
                        type: authActions.STORE_AUTH_CHECK_PUBLIC_INFO,
                        response: publicInfoUpdate,
                    });
                }
            } catch (err) {
                const status = err?.response?.status;
                const data = err?.response?.data;
                if (data) {
                    if (data?.backend_version != null) {
                        dispatch({
                            type: authActions.STORE_BACKEND_VERSION,
                            response: data.backend_version,
                        });
                    }
                    dispatch({
                        type: authActions.STORE_AUTH_CHECK_PUBLIC_INFO,
                        response: {
                            workspaceName: data?.workspace_name ?? null,
                            workspaceHighlightColor: data?.workspace_highlight_color ?? null,
                            backendVersion: data?.backend_version ?? null,
                        },
                    });
                }
                if (isAuthCheckUnauthorized(status)) {
                    dispatch({
                        type: authActions.SIGN_IN_FAILED,
                    });
                    return;
                }
                const maxAttempts = 3;
                if (attempt < maxAttempts - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    return runAuthCheck(attempt + 1);
                }
                NotificationService.error(<RestErrorNotification error={err} />);
            }
        };

        await runAuthCheck();
        await navigateToNewRoute();
    });

    useEffect(() => {
        const schemaKey = "formSchema.json";
        if (schemaFormSchema) {
            if (!ajv.schemas[schemaKey]) {
                ajv.addSchema(schemaFormSchema, schemaKey);
            }
        } else {
            fetchSchemaFormSchema(dispatch);
        }
    }, [dispatch, schemaFormSchema]);

    useEffect(() => {
        const fetchBootstrapUserName = async (dispatch, workspaceID) => {
            const queryStr = `query ($id: String!){
            workspace(id: $id) {
                bootstrap_user_name
            }
        }`;
            const params = {
                query: queryStr,
                variables: {
                    id: workspaceID,
                },
            };
            try {
                const response = await graphqlPost(params);
                dispatch({
                    type: dataStoreActions.STORE_DATA,
                    key: bootstrapUserNameKey,
                    data: response?.data?.workspace?.bootstrap_user_name,
                });
            } catch (error) {
                console.error(error);
            }
        };
        if (workspaceID) {
            fetchBootstrapUserName(dispatch, workspaceID);
        }
    }, [dispatch, workspaceID]);

    useEffect(() => {
        const handleLocationChange = () => {
            if (allowedToClearNotifications.current) {
                // Clear Page Notifications
                dispatch({
                    type: notificationManagerActions.REMOVE_PAGE_NOTIFICATIONS,
                    initiator: "router",
                });
            }
            allowedToClearNotifications.current = true;
        };
        handleLocationChange();
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const [route, setRoute] = useState({
        to: location.pathname,
        from: location.pathname,
        prevSearch: "",
        currentSearch: location.search,
    });

    // Narrow-viewport gate: the dense desktop-first surfaces are shown behind a
    // "not optimized for mobile" interstitial (login + the rendered dashboard
    // view stay usable). `mobileDismissed` persists the user's "continue anyway"
    // choice for the session. See operator-task-fe-mobile-not-optimized-interstitial.
    const isNarrow = useIsNarrow();
    const [mobileDismissed, setMobileDismissed] = useState(isMobileInterstitialDismissed);
    const continueOnMobile = () => {
        dismissMobileInterstitial();
        setMobileDismissed(true);
    };

    const prevUrl = useMemo(() => {
        const prevUrl = route.from + route.prevSearch;
        const isSignedInRoute = !signedOutRoutesArray.includes(route.from);

        if (!isSignedIn && isSignedInRoute && prevUrl !== "/") {
            const protocol = window.location.protocol;
            const host = window.location.hostname;
            const port = window.location.port?.length ? `:${window.location.port}` : "";
            return `${protocol}//${host}${port}${prevUrl}`;
        } else {
            return "";
        }
    }, [isSignedIn, route.from, route.prevSearch]);

    useEffect(() => {
        setRoute((prev) => ({
            to: location.pathname,
            from: prev.to,
            currentSearch: location.search,
            prevSearch: prev.currentSearch,
        }));
    }, [location]);

    // Memoize the context value so a background poll (git-log/projects/bundles)
    // that re-renders RouterManager does not hand consumers (e.g. the whole
    // ResourcesSingle record editor) a fresh object identity and force them to
    // re-render / pierce their React.memo. `ajv` is a stable module-level const.
    const routerContextValue = useMemo(
        () => ({
            route,
            ajv,
            filteredSchemaFormSchema,
            allUncommittedData,
            gitLockData,
        }),
        [route, filteredSchemaFormSchema, allUncommittedData, gitLockData]
    );

    // execution live monitor hotkey (ctrl + m)
    useEffect(() => {
        const keydownHandler = (e) => {
            if (
                e.ctrlKey &&
                e.key === "m" &&
                isSignedIn &&
                location.pathname !== confirmEmailPath &&
                !isMonacoEditorActiveElement()
            ) {
                e.preventDefault();
                history.push(`${getWorkspaceUrl()}?${executionsPathFragment}`);
            }
        };
        window.addEventListener("keydown", keydownHandler);
        return () => {
            window.removeEventListener("keydown", keydownHandler);
        };
    }, [history, workspaceID, isSignedIn, location.pathname]);

    const signedOutRoutes = [
        <Route key="Login" exact path={loginPath} component={Login} />,
        <Route key="RecoverAccount" exact path={recoverPath} component={RecoverAccount} />,
        <Route key="ActivateAccount" path={activatePath} exact component={ActivateAccount} />,
        <Route key="ConfirmEmail" path={confirmEmailPath} exact component={ConfirmEmail} />,
        <Route key="ResetPassword" exact path={resetPath} component={ResetPassword} />,
        <Redirect key="RedirectToLogin" to={loginPath} />,
    ];

    // Sub-page routers: /workspace, /project, /bundle dispatch based on ?subPage query flag.
    // All other record types get a generic router that handles ?logs.
    const workspaceSubPageRender = () => {
        const p = queryParams;
        if (settingsPathFragment in p) return <WorkspaceSettings />;
        if (storageAnalysisSubPageKey in p) return <StorageAnalysis />;
        if (bundleManagerPathFragment in p) return <BundleManager />;
        if (advancedSearchPathFragment in p) return <AdvancedSearch />;
        if (usersRolesPathFragment in p) return <IdentityManagement />;
        if (executionsPathFragment in p) return <ExecutionsListing />;
        if (logSubPageKey in p) return <ContainerLogList />;
        return <ResourcesSingle />;
    };
    const projectSubPageRender = () => {
        const p = queryParams;
        if (executionsPathFragment in p) return <ExecutionsListing />;
        if ("canvas" in p) return <Canvas />;
        if (logSubPageKey in p) return <ContainerLogList />;
        return <ResourcesSingle />;
    };
    const bundleSubPageRender = () => {
        const p = queryParams;
        if (executionsPathFragment in p) return <ExecutionsListing />;
        if ("canvas" in p) return <Canvas />;
        if (logSubPageKey in p) return <ContainerLogList />;
        return <ResourcesSingle />;
    };
    const genericSubPageRender = () => {
        const p = queryParams;
        if (logSubPageKey in p) return <ContainerLogList />;
        return <ResourcesSingle />;
    };

    const signedInRoutes = [
        // ── Backward-compat redirects for old path-based sub-page URLs ──────────────────
        <Route
            key="compat-workspace-executions"
            exact
            path={`/workspace/:id/${executionsPathFragment}`}
            render={({ match, location: loc }) => {
                const extraSearch = stripFilterFromSearch(loc.search);
                const extra = extraSearch ? `&${extraSearch}` : "";
                return <Redirect to={`${getWorkspaceUrl()}?${executionsPathFragment}${extra}`} />;
            }}
        />,
        <Route
            key="compat-project-executions"
            exact
            path={`/project/:id/${executionsPathFragment}`}
            render={({ match, location: loc }) => {
                const extraSearch = stripFilterFromSearch(loc.search);
                const extra = extraSearch ? `&${extraSearch}` : "";
                return <Redirect to={`${getRelativeUrl("project", match.params.id, "id")}&${executionsPathFragment}${extra}`} />;
            }}
        />,
        <Route
            key="compat-bundle-executions"
            exact
            path={`/bundle/:id/${executionsPathFragment}`}
            render={({ match, location: loc }) => {
                const extraSearch = stripFilterFromSearch(loc.search);
                const extra = extraSearch ? `&${extraSearch}` : "";
                return <Redirect to={`${getRelativeUrl("bundle", match.params.id, "id")}&${executionsPathFragment}${extra}`} />;
            }}
        />,
        <Route
            key="compat-workspace-settings"
            exact
            path={`/workspace/:id/${settingsPathFragment}`}
            render={({ match }) => (
                <Redirect to={getWorkspaceLink("", settingsPathFragment)} />
            )}
        />,
        <Route
            key="compat-workspace-storage-analysis"
            exact
            path={`/workspace/:id/${storageAnalysisSubPageKey}`}
            render={({ match }) => (
                <Redirect to={getWorkspaceLink("", storageAnalysisSubPageKey)} />
            )}
        />,
        <Route
            key="compat-workspace-bundle-manager"
            exact
            path={`/workspace/:id/${bundleManagerPathFragment}`}
            render={({ match }) => (
                <Redirect to={getWorkspaceLink("", bundleManagerPathFragment)} />
            )}
        />,
        <Route
            key="compat-project-canvas"
            exact
            path={`/project/:id/${canvasPathFragment}/:canvasType`}
            render={({ match }) => (
                <Redirect to={`${getRelativeUrl("project", match.params.id, "id")}&canvas=${match.params.canvasType}`} />
            )}
        />,
        <Route
            key="compat-bundle-canvas"
            exact
            path={`/bundle/:id/${canvasPathFragment}/:canvasType`}
            render={({ match }) => (
                <Redirect to={`${getRelativeUrl("bundle", match.params.id, "id")}&canvas=${match.params.canvasType}`} />
            )}
        />,
        <Route
            key="compat-logs"
            exact
            path={`/:type/:id/${logListPathFragment}`}
            render={({ match, location: loc }) => {
                const extraSearch = stripFilterFromSearch(loc.search);
                const extra = extraSearch ? `&${extraSearch}` : "";
                return <Redirect to={`${getRelativeUrl(match.params.type, match.params.id, "id")}&${logSubPageKey}${extra}`} />;
            }}
        />,
        // ── Backward-compat redirects for legacy standalone workspace sub-page paths ──
        <Route
            key="compat-advanced-search"
            exact
            path={advancedSearchPathLegacy}
            render={() => <Redirect to={mapScreensToPaths.advancedSearch} />}
        />,
        <Route
            key="compat-users-roles"
            exact
            path={usersRolesManagementPathLegacy}
            render={() => <Redirect to={mapScreensToPaths.userRoleManagement} />}
        />,
        <Route
            key="compat-trash"
            exact
            path={deletedPathLegacy}
            render={() => <Redirect to={mapScreensToPaths.trash} />}
        />,
        // ────────────────────────────────────────────────────────────────────────────────
        // Dashboard is handled separately so the DEFAULT (no flag) renders the
        // rendered DashboardView, while the `?edit` flag renders ResourcesSingle
        // (the config editor) — both at the same /dashboard path. See
        // operator-task-fe-dashboard-default-to-view-with-edit-button.
        isNewRecord && resourceName === "dashboard"
            ? [
                  <Route
                      key={`dashboard_new${newName}`}
                      exact
                      path="/dashboard/new"
                      component={NewResourceSingle}
                  />,
              ]
            : [
                  <Route
                      key={`dashboard${recordId}${keyToForceUpdate ?? ""}`}
                      exact
                      path="/dashboard"
                      render={() =>
                          dashboardEditPathFragment in queryParams ? (
                              <ResourcesSingle />
                          ) : (
                              <DashboardView />
                          )
                      }
                  />,
              ],
        // Backward-compat redirect: old /dashboard/<id>/view links → the rendered
        // dashboard (now the default, no route flag).
        <Route
            key="dashboard-view-compat"
            exact
            path={`/dashboard/:id/${dashboardViewPathFragment}`}
            render={({ match }) => (
                <Redirect to={getRelativeUrl("dashboard", match.params.id, "id")} />
            )}
        />,
        recordList?.map((resource) => {
            const label = getTypedLowerCase(resource);
            if (label === "dashboard") return null; // handled above
            const subPageRender =
                label === "workspace"
                    ? workspaceSubPageRender
                    : label === "project"
                    ? projectSubPageRender
                    : label === "bundle"
                    ? bundleSubPageRender
                    : genericSubPageRender;
            return isNewRecord
                ? [
                      <Route
                          key={`${label}_new${newName}`}
                          exact
                          path={`/${label}/new`}
                          component={NewResourceSingle}
                      />,
                  ]
                : [
                      <Route
                          key={`${label}${recordId}${keyToForceUpdate ?? ""}`}
                          exact
                          path={`/${label}`}
                          render={subPageRender}
                      />,
                  ];
        }),
        metaTypes.map((metaType) => [
            <Route
                key={`${metaType}${recordId}`}
                exact
                path={`/${metaType}`}
                component={MetaType}
            />,
        ]),
        <Route key="WorkspaceDashboard" exact path="/">
            <Redirect to={getWorkspaceUrl()} />
        </Route>,
        <Route key="Login" exact path={loginPath}>
            {canRedirectToWorkspace ? (
                <Redirect to={getWorkspaceUrl()} />
            ) : (
                <div className="RouterManager loader">
                    <CircularProgress disableShrink className="spinner" />
                </div>
            )}
        </Route>,
        <Route key="trashBin" exact path={mapScreensToPaths.trash} component={TrashBin} />,
        <Route key="SignOut" exact path={signOutPath} component={SignOut} />,
        <Route key="Error" component={Error} />,
    ];

    // always render router and websocket
    const routerAndWebSocket = (
        <>
            <RouterChangeHandler isNewRecord={isNewRecord} />
            <MultiWebsocket isSignedIn={isSignedIn} />
        </>
    );

    const loader = (
        <div className="RouterManager loader">
            <CircularProgress disableShrink className="spinner" />
        </div>
    );

    // this is the initial state. we are probably sending a request to check if the user is signed in
    // show a spinner until the request is finished
    if (isSignedIn === null && !workspaceID) {
        return loader;
    }

    // special case for the confirm email page
    if (isSignedIn && workspaceID && location.pathname === confirmEmailPath) {
        return (
            <>
                <AuthPage prevUrl={prevUrl}>
                    <Route
                        key="ConfirmEmail"
                        path={confirmEmailPath}
                        exact
                        component={ConfirmEmail}
                    />
                </AuthPage>
            </>
        );
    }

    // if the user is signed in, render the default page
    if (isSignedIn && workspaceID) {
        if (!canConnect) {
            return loader;
        }
        return (
            <>
                {routerAndWebSocket}
                <DefaultPage kiosk={isKiosk}>
                    {
                        <Suspense fallback={<></>}>
                            <RouterContext.Provider value={routerContextValue}>
                                <DndProvider backend={HTML5Backend}>
                                    <NavigationPromptProvider>
                                        <RouteErrorBoundary>
                                            {shouldShowMobileInterstitial({
                                                isNarrow,
                                                dismissed: mobileDismissed,
                                                pathname: location.pathname,
                                            }) ? (
                                                <MobileInterstitial onContinue={continueOnMobile} />
                                            ) : (
                                                <Switch>{signedInRoutes}</Switch>
                                            )}
                                        </RouteErrorBoundary>
                                    </NavigationPromptProvider>
                                </DndProvider>
                            </RouterContext.Provider>
                        </Suspense>
                    }
                </DefaultPage>
            </>
        );
    }

    // if the user is not signed in, render the auth page
    if (isSignedIn === false) {
        return (
            <>
                <AuthPage prevUrl={prevUrl}>
                    <Switch>{signedOutRoutes}</Switch>
                </AuthPage>
            </>
        );
    }

    // there is a brief gap when the user is signed in, but the workspaceID
    // has not been updated yet. in this case, render loader
    return loader;
};

RouterManager.defaultProps = {
    location: {},
};

RouterManager.propTypes = {
    location: PropTypes.oneOfType([PropTypes.object]),
};

export default withRouter(RouterManager);
